/**
 * #486 Morphing Liquid Metal（变形液态金属）
 * 
 * 2026 设计趋势：液态金属 + 流体动画
 * 
 * 核心效果：
 * - 金属质感的圆形/椭圆形态变形
 * - 多层金属光泽动画
 * - 流动的反射效果
 * - 阴影 + 高光增强立体感
 * 
 * 应用场景：
 * - 科技产品发布会
 * - 品牌视觉设计
 * - 小红书科技内容
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

export const MorphingLiquidMetal: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Spring 配置（平滑入场）
  const scaleProgress = spring({
    frame,
    fps: 30,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  // 金属形态变形（使用正弦波）
  const morphSpeed = 0.03;
  const morphAmount = 0.3;

  // 主金属球体变形参数
  const radiusX = interpolate(
    Math.sin(frame * morphSpeed),
    [-1, 1],
    [180, 220]
  );
  const radiusY = interpolate(
    Math.cos(frame * morphSpeed * 1.3),
    [-1, 1],
    [180, 220]
  );

  // 旋转角度（缓慢旋转）
  const rotation = frame * 0.5;

  // 金属光泽动画
  const gradientAngle = (frame * 2) % 360;

  // 高光点动画
  const highlightX = interpolate(
    Math.sin(frame * 0.05),
    [-1, 1],
    [0.35, 0.65]
  );
  const highlightY = interpolate(
    Math.cos(frame * 0.04),
    [-1, 1],
    [0.35, 0.45]
  );

  // 辅助金属球体（小）
  const smallOrbit = (offset: number) => {
    const angle = (frame * 2 + offset) % 360;
    const distance = 280;
    return {
      x: Math.cos((angle * Math.PI) / 180) * distance,
      y: Math.sin((angle * Math.PI) / 180) * distance * 0.5,
    };
  };

  // 金属渐变色（钛金属/液态银）
  const metalGradient = `
    radial-gradient(
      ellipse ${radiusX}px ${radiusY}px at ${highlightX * 100}% ${highlightY * 100}%,
      #e8e8e8 0%,
      #c0c0c0 30%,
      #a0a0a0 50%,
      #808080 70%,
      #606060 100%
    )
  `;

  // 高光层渐变（更亮的高光）
  const highlightGradient = `
    radial-gradient(
      ellipse ${radiusX * 0.3}px ${radiusY * 0.3}px at ${highlightX * 100}% ${highlightY * 100}%,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
    )
  `;

  // 反射层渐变（底部反射）
  const reflectionGradient = `
    radial-gradient(
      ellipse ${radiusX}px ${radiusY * 0.2}px at 50% 85%,
      rgba(150, 150, 150, 0.4) 0%,
      transparent 100%
    )
  `;

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(circle, #1a1a2e 0%, #0f0f1e 100%)',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 辅助光效背景 */}
      <div
        style={{
          position: 'absolute',
          width: 600,
          height: 600,
          background: `radial-gradient(circle, rgba(100, 149, 237, 0.1) 0%, transparent 70%)`,
          transform: `translateY(${Math.sin(frame * 0.02) * 50}px)`,
        }}
      />

      {/* 辅助金属球体 1（左） */}
      <div
        style={{
          position: 'absolute',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `
            radial-gradient(
              ellipse 60px 60px at 30% 30%,
              #d0d0d0 0%,
              #a0a0a0 50%,
              #707070 100%
            )
          `,
          boxShadow: `
            inset 0 -10px 20px rgba(0, 0, 0, 0.3),
            0 20px 40px rgba(0, 0, 0, 0.5)
          `,
          left: `calc(50% + ${smallOrbit(0).x}px - 40px)`,
          top: `calc(50% + ${smallOrbit(0).y}px - 40px)`,
          transform: `scale(${scaleProgress}) rotate(${rotation * 0.5}deg)`,
        }}
      />

      {/* 辅助金属球体 2（右） */}
      <div
        style={{
          position: 'absolute',
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: `
            radial-gradient(
              ellipse 45px 45px at 35% 35%,
              #e0e0e0 0%,
              #b0b0b0 50%,
              #808080 100%
            )
          `,
          boxShadow: `
            inset 0 -8px 16px rgba(0, 0, 0, 0.3),
            0 15px 30px rgba(0, 0, 0, 0.5)
          `,
          left: `calc(50% + ${smallOrbit(120).x}px - 30px)`,
          top: `calc(50% + ${smallOrbit(120).y}px - 30px)`,
          transform: `scale(${scaleProgress}) rotate(${-rotation * 0.3}deg)`,
        }}
      />

      {/* 主金属球体 */}
      <div
        style={{
          position: 'absolute',
          width: radiusX * 2,
          height: radiusY * 2,
          borderRadius: '50%',
          background: metalGradient,
          boxShadow: `
            inset 0 -20px 40px rgba(0, 0, 0, 0.4),
            inset 0 20px 40px rgba(255, 255, 255, 0.1),
            0 40px 80px rgba(0, 0, 0, 0.6)
          `,
          transform: `scale(${scaleProgress}) rotate(${rotation}deg)`,
        }}
      />

      {/* 高光层 */}
      <div
        style={{
          position: 'absolute',
          width: radiusX * 2,
          height: radiusY * 2,
          borderRadius: '50%',
          background: highlightGradient,
          transform: `scale(${scaleProgress})`,
          mixBlendMode: 'screen',
        }}
      />

      {/* 反射层 */}
      <div
        style={{
          position: 'absolute',
          width: radiusX * 2,
          height: radiusY * 2,
          borderRadius: '50%',
          background: reflectionGradient,
          transform: `scale(${scaleProgress})`,
          opacity: 0.6,
        }}
      />

      {/* 辅助金属球体 3（底部） */}
      <div
        style={{
          position: 'absolute',
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: `
            radial-gradient(
              ellipse 38px 38px at 32% 32%,
              #c8c8c8 0%,
              #989898 50%,
              #686868 100%
            )
          `,
          boxShadow: `
            inset 0 -6px 12px rgba(0, 0, 0, 0.3),
            0 12px 24px rgba(0, 0, 0, 0.5)
          `,
          left: `calc(50% + ${smallOrbit(240).x}px - 25px)`,
          top: `calc(50% + ${smallOrbit(240).y}px - 25px)`,
          transform: `scale(${scaleProgress}) rotate(${rotation * 0.7}deg)`,
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'PingFang SC, sans-serif',
          fontSize: 72,
          fontWeight: 'bold',
          color: '#e8e8e8',
          textShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          transform: `translateY(${interpolate(scaleProgress, [0, 1], [-50, 0])}px)`,
          opacity: scaleProgress,
        }}
      >
        液态金属
      </div>

      {/* 副标题 */}
      <div
        style={{
          position: 'absolute',
          top: 170,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'PingFang SC, sans-serif',
          fontSize: 30,
          color: '#a0a0a0',
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
          transform: `translateY(${interpolate(scaleProgress, [0, 1], [-30, 0])}px)`,
          opacity: scaleProgress,
        }}
      >
        Morphing Liquid Metal
      </div>

      {/* 说明文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'PingFang SC, sans-serif',
          fontSize: 34,
          color: '#808080',
          textShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
          transform: `translateY(${interpolate(scaleProgress, [0, 1], [30, 0])}px)`,
          opacity: scaleProgress,
        }}
      >
        2026 设计趋势 · 金属质感 · 流体动画
      </div>
    </AbsoluteFill>
  );
};
